import { defineDocument } from '../../schema';
export const gameSecondOpinionsDocument = defineDocument({
    id: 'game:second-opinions',
    kind: 'game',
    slug: 'second-opinions',
    route: '/discover/games/second-opinions',
    seo: {
        title: 'Discover Second Opinions',
        description: 'A social deduction game where Good Doctors heal Poppy and Bad Doctors secretly sabotage. Vote on treatments, catch traitors, or flatline the patient.',
        imageAsset: 'coverSecondOpinions',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Game',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Second Opinions',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'A deception hidden-role game where Good Doctors try to heal Poppy while Bad Doctors try to hurt. Doctors suspected of malpractice can be voted out.',
            },
        ],
        imageAsset: 'coverSecondOpinions',
        imageAlt: 'Second Opinions game cover',
        showDescription: true,
        background: 'blurred-image',
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'game-description',
            title: [
                {
                    type: 'text',
                    text: 'Game Description',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy is in trouble and headed into surgery. Good Doctors work together to heal our beloved patient, while Bad Doctors secretly try to push Poppy toward the great beyond. ',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: ' Each turn, the Chief of Medicine and their Committee select a HEAL or HURT treatment. The group can accuse suspicious doctors of malpractice and eliminate them from the game. ',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: ' Good Doctors win by fully healing Poppy or eliminating all Bad Doctors. If Poppy flatlines or all Good Doctors are eliminated, Bad Doctors win and we all lose a bit of faith in the medical system.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'facts',
            title: [
                {
                    type: 'text',
                    text: 'Facts',
                },
            ],
            blocks: [
                {
                    type: 'facts',
                    items: [
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Players',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '5-16 players',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Game Type',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Team',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Mechanics',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'Selection, Voting, Deduction, Elimination',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Average Duration',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '~20 minutes',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Release Date',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: 'December 4, 2025',
                                },
                            ],
                        },
                        {
                            label: [
                                {
                                    type: 'text',
                                    text: 'Cost',
                                },
                            ],
                            value: [
                                {
                                    type: 'text',
                                    text: '1 Loon',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'clips',
            title: [
                {
                    type: 'text',
                    text: 'Clips',
                },
            ],
            widgets: [
                {
                    type: 'clipScroller',
                    gameId: '6921e33b0eef394c89845fb8',
                },
            ],
        },
        {
            id: 'play-sequence',
            title: [
                {
                    type: 'text',
                    text: 'Play Sequence',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Players are secretly assigned to a team at the start of the game: Good Doctors or Bad Doctors. Roles do not change throughout the game. Approximately 2/3 of players are Good Doctors and 1/3 are Bad Doctors. Bad Doctors know who each other are, but Good Doctors do not know anyone else's role.",
                        },
                    ],
                },
                {
                    type: 'list',
                    ordered: true,
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'A player is selected as the Chief of Medicine. They are presented with three random treatment options. Treatments can either HEAL or HURT Poppy, and the deck is stacked with 40% HEAL and 60% HURT cards. The Chief must discard one treatment option and pick two players to join their treatment committee.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'The three players on the treatment committee vote for which of the two treatments they want to apply to Poppy.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: "The treatment with the majority of votes is applied to Poppy, moving Poppy's health forward (HEAL) or backward (HURT).",
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'A long voting window opens for players to discuss and accuse suspicious doctors of malpractice. During the window, players can vote to eliminate a doctor or abstain. Players can change their vote up until the last player votes or time runs out.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'If a doctor receives the majority of the vote, they are eliminated from the game. Eliminated doctors are out and their role is NOT revealed.',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Repeat until one team achieves their win condition.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'scoring-winning',
            title: [
                {
                    type: 'text',
                    text: 'Scoring & Winning',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Poppy's health is tracked in 7 stages. The game starts with Poppy in the middle stage. When a treatment is applied, Poppy's health moves +1 stage for HEAL treatments or -1 stage for HURT treatments. A winning strategy requires careful observation, deduction, deception, and teamwork.",
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Winning conditions:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Good Doctors: fully healing Poppy (+3 stages) OR eliminating all Bad Doctors.',
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Bad Doctors: flatlining Poppy (-3 stages) OR eliminating all Good Doctors.',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'generated-content',
            title: [
                {
                    type: 'text',
                    text: 'Generated Content',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Players:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'None',
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Poppy AI:',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'Treatment options',
                            },
                        ],
                    ],
                },
            ],
        },
        {
            id: 'other-notes',
            title: [
                {
                    type: 'text',
                    text: 'Other Notes',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Brain power required: high',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Popular with: fans of hidden role or deduction games, strategic thinkers, in-person games',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Read more: ',
                        },
                        {
                            type: 'link',
                            href: '/blog/scrub-into-second-opinions',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Second Opinions blog post',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'songs',
            title: [
                {
                    type: 'text',
                    text: 'Songs',
                },
            ],
            widgets: [
                {
                    type: 'songTable',
                    gameId: '6921e33b0eef394c89845fb8',
                },
            ],
        },
    ],
});
//# sourceMappingURL=second-opinions.js.map